import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { RequerimentoRenovacaoRegularidadePjService } from '@/core/api/sicom-api/registro/prestador/relatorios/requerimento-renovacao-regularidade-pj/requerimento-renovacao-regularidade-pj.service';
import {
    ReportRequerimentoRenovacaoRegularidadePjRequest,
    ReportRequerimentoRenovacaoRegularidadePjResponse
} from '@/core/api/sicom-api/registro/prestador/relatorios/requerimento-renovacao-regularidade-pj/requerimento-renovacao-regularidade-pj.model';
import { RequerimentoRenovacaoRegularidadePjJobService } from '@/core/services/requerimento-renovacao-regularidade-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { DateUtils } from '@/shared/utils/date-utils';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportRequerimentoRenovacaoRegularidadePjRequest;
type Resp = ReportRequerimentoRenovacaoRegularidadePjResponse;

/** Controles tipados do formulário do requerimento. */
interface RequerimentoRenovacaoRegularidadePjFormControls {
    dataAlteracao: FormControl<Date | null>;
    alteracaoCorpoClinico: FormControl<boolean>;
}

/**
 * Relatório PDF assíncrono: Requerimento de Renovação de Regularidade PJ (formulário).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`), a data da última alteração do documento de
 * constituição e a marcação do corpo clínico (alterado / sem alteração).
 */
@Component({
    selector: 'app-requerimento-renovacao-regularidade-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, DatePicker, RadioButtonModule, RelatorioContainer, CanDirective],
    templateUrl: './requerimento-renovacao-regularidade-pj.html',
    styleUrl: './requerimento-renovacao-regularidade-pj.scss'
})
export class ReportRequerimentoRenovacaoRegularidadePjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly requerimentoService = inject(RequerimentoRenovacaoRegularidadePjService);
    private readonly pessoaService = inject(PessoaService);
    readonly requerimentoJobService = inject(RequerimentoRenovacaoRegularidadePjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.requerimentoJobService.job$;

    form: FormGroup<RequerimentoRenovacaoRegularidadePjFormControls> = this.formBuilder.group<RequerimentoRenovacaoRegularidadePjFormControls>({
        dataAlteracao: new FormControl<Date | null>(null, Validators.required),
        alteracaoCorpoClinico: new FormControl<boolean>(false, { nonNullable: true })
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.requerimentoJobService;
    }

    /**
     * Monta o request do prestador selecionado + campos do formulário.
     * Retorna null (e avisa) se faltar CRM/UF ou se a data obrigatória não estiver preenchida.
     */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o requerimento.'
            });
            return null;
        }

        // Regra do legado: a data da última alteração é obrigatória para emitir.
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Informe a data da última alteração do documento.'
            });
            return null;
        }

        const { dataAlteracao, alteracaoCorpoClinico } = this.form.getRawValue();

        return {
            nuCrm,
            sgUf,
            dataAlteracao: DateUtils.formatDateToApi(dataAlteracao),
            alteracaoCorpoClinico: !!alteracaoCorpoClinico
        };
    }

    protected exportar(request: Req) {
        return this.requerimentoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.form.reset({ dataAlteracao: null, alteracaoCorpoClinico: false });
        this.limparJob();
    }
}
